import { useEffect, useMemo, useState } from "react";
import { usePublicClient } from "wagmi";
import { Search, ShieldAlert, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { isAddress, fmtUsd, fmtNum } from "../lib/format";
import { useWalletHoldings, sortByHoldings } from "../lib/holdings";
import { erc20Abi } from "../lib/abis";
import { OriginalBadge } from "./OriginalBadge";
import { Loading, ErrorState } from "./State";

const abs = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const logoCache = new Map();
let pending = new Map();
let timer = null;
const ADDR = /^0x[0-9a-fA-F]{40}$/;
// Batches unknown-logo lookups from every TokenLogo on the page into one request.
const resolveLogo = (addr) => {
  const a = addr.toLowerCase();
  if (logoCache.has(a)) return logoCache.get(a);
  const p = new Promise((res) => {
    pending.set(a, res);
    if (!timer) timer = setTimeout(async () => {
      const batch = pending; pending = new Map(); timer = null;
      let data = {};
      try { data = (await api.post("/token-page/logos", { addresses: [...batch.keys()] })).data; } catch { batch.forEach((_, k) => logoCache.delete(k)); }
      batch.forEach((r, k) => r(data[k] || null));
    }, 60);
  });
  logoCache.set(a, p);
  return p;
};

export const TokenLogo = ({ t, size = 32 }) => {
  const [stage, setStage] = useState(0);
  const [found, setFound] = useState(null);
  const addr = t?.address;
  const own = t?.logo || t?.image_url;
  useEffect(() => {
    setFound(null);
    if (!addr || !ADDR.test(addr) || (own && t?.logo_fallback)) return;
    let on = true;
    resolveLogo(addr).then((r) => on && setFound(r));
    return () => { on = false; };
  }, [addr, own, t?.logo_fallback]);
  const primary = abs(own || found?.logo);
  const fallback = t?.logo_fallback || found?.logo_fallback || (own && found?.logo !== own ? abs(found?.logo) : null);
  const light = t?.logo_light || found?.logo_light;
  useEffect(() => setStage(0), [primary]);
  const src = stage === 0 ? primary : stage === 1 ? fallback : null;
  const onErr = () => setStage((s) => (s === 0 && fallback ? 1 : 2));
  const stock = !!(t?.logo_fallback || found?.logo_fallback);
  if (!src) return <div style={{ width: size, height: size }} className="hex shrink-0 bg-lime/10 grid place-items-center text-[10px] font-bold text-lime">{(t?.symbol || "?").slice(0, 3)}</div>;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={`token-logo-${t?.symbol}`}>
      <img src={src} alt={t?.symbol || ""} loading="lazy" onError={onErr}
        className={`h-full w-full rounded-full ${stock && stage === 0 ? `object-contain p-[12%] ${light ? "bg-[#1c2333] ring-1 ring-white/10" : "bg-white"}` : "object-cover bg-white/5"}`} />
      {stock && stage === 1 && size >= 28 && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded bg-black/90 px-1 text-[8px] font-mono font-bold leading-tight text-lime ring-1 ring-lime/40">{t.symbol.slice(0, 5)}</span>}
    </div>
  );
};

const TokenRow = ({ t, onPick }) => (
  <button data-testid={`token-option-${t.symbol}`} onClick={() => onPick(t)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-lime/5 text-left">
    <TokenLogo t={t} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold flex items-center gap-1.5 min-w-0"><span className="truncate max-w-[160px]">{t.symbol}</span> {t.verified ? <BadgeCheck className="h-3.5 w-3.5 text-lime" /> : <ShieldAlert className="h-3.5 w-3.5 text-warning" />}
        {t.kind === "robinhood_stock_token" && <span className="rounded bg-lime/10 px-1 text-[9px] font-bold text-lime">STOCK TOKEN</span>}
        <OriginalBadge address={t.address} />
        {t.kind === "crypto" && !t.verified && <span className="rounded bg-white/5 px-1 text-[9px] font-bold text-slate-400">INDEXED</span>}</p>
      <p className="text-[11px] text-slate-500 truncate">{t.name} {t.address !== "native" && `· ${t.address.slice(0, 10)}…`}</p>
    </div>
    {t.held ? <span data-testid={`token-balance-${t.symbol}`} className="text-xs font-mono text-right shrink-0 text-lime">{fmtNum(t.balance, 6)}<br /><span className="text-[9px] text-slate-500">in wallet</span></span>
      : t.liquidity_usd > 0 && <span className={`text-[10px] font-mono text-right shrink-0 ${t.liquidity_usd < 1000 ? "text-warning" : "text-slate-500"}`}>{fmtUsd(t.liquidity_usd)}<br />liq.</span>}
  </button>
);

export const TokenSelect = ({ open, onOpenChange, tokens, onSelect }) => {
  const [q, setQ] = useState("");
  const [imp, setImp] = useState({ loading: false, token: null, error: null });
  const pc = usePublicClient({ chainId: 4663 });

  const [remote, setRemote] = useState([]);
  useEffect(() => {
    if (!q) return setRemote([]);
    const t = setTimeout(() => api.get("/tokens/all", { params: { q, limit: 60 } }).then((r) => setRemote(r.data.tokens)).catch(() => setRemote([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const local = tokens.filter((t) => !q || t.symbol.toLowerCase().includes(ql) || t.name?.toLowerCase().includes(ql) || t.address.toLowerCase() === ql);
    const seen = new Set(local.map((t) => t.address.toLowerCase()));
    return [...local, ...remote.filter((t) => !seen.has(t.address.toLowerCase()))];
  }, [tokens, q, remote]);

  useEffect(() => {
    setImp({ loading: false, token: null, error: null });
    if (!isAddress(q) || filtered.length) return;
    setImp({ loading: true, token: null, error: null });
    Promise.all(["symbol", "name", "decimals"].map((fn) => pc.readContract({ address: q, abi: erc20Abi, functionName: fn })))
      .then(([symbol, name, decimals]) => setImp({ loading: false, error: null, token: { address: q, symbol, name, decimals: Number(decimals), verified: false } }))
      .catch((e) => setImp({ loading: false, token: null, error: "Not an ERC-20 contract on Robinhood Chain" }));
  }, [q, filtered.length, pc]);

  const pick = (t) => { onSelect(t); onOpenChange(false); setQ(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12161F] border-lime/20 max-w-md text-slate-100" data-testid="token-select-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-wide">Select a token</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input data-testid="token-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value.trim())} placeholder="Search name or paste contract address" className="field pl-9" />
        </div>
        <div className="max-h-[400px] overflow-y-auto -mx-2" data-testid="token-select-list">
          {!q && filtered[0]?.held && <p className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wider text-lime">Your tokens</p>}
          {filtered.slice(0, 150).map((t, i) => (
            <div key={t.address}>
              {!q && i > 0 && filtered[i - 1].held && !t.held && <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 border-t border-white/5">All tokens</p>}
              <TokenRow t={t} onPick={pick} />
            </div>
          ))}
          {filtered.length > 150 && <p className="px-3 py-2 text-[11px] text-slate-500">{filtered.length - 150} more — refine your search</p>}
          <p className="px-3 py-2 text-[10px] text-slate-600">Includes every token found in indexed pools on all Robinhood Chain DEXes. INDEXED tokens are not reviewed by HIVEVORA — anyone can create a token with any name. Check the contract address.</p>
          {imp.loading && <Loading label="Reading contract on-chain" />}
          {imp.error && <ErrorState message={imp.error} />}
          {imp.token && (
            <div className="px-2">
              <p data-testid="token-import-warning" className="text-[11px] text-warning px-1 pb-2">Unlisted token read from chain. Anyone can create a token with any name — verify the address yourself.</p>
              <TokenRow t={imp.token} onPick={pick} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const useTokenList = (base) => {
  const [listed, setListed] = useState([]);
  const [official, setOfficial] = useState([]);
  const [logos, setLogos] = useState({});
  useEffect(() => {
    Promise.all([api.get("/tokens/all", { params: { limit: 400 } }), api.get("/tokens/all", { params: { kind: "robinhood_stock_token", limit: 400, sort: "symbol" } })])
      .then(([a, b]) => setOfficial([...a.data.tokens, ...b.data.tokens])).catch((e) => console.warn(errMsg(e)));
    api.get("/tokens/base-logos").then((r) => setLogos(r.data)).catch(() => {});
    api.get("/listings").then((r) => setListed(r.data.map((l) => ({ address: l.contract_address, symbol: l.onchain.symbol, name: l.onchain.name,
      decimals: l.onchain.decimals, logo: l.metadata.logo_url, verified: false, listed: true })))).catch((e) => console.warn(errMsg(e)));
  }, []);
  const all = useMemo(() => {
    const weth = base.find((t) => t.symbol === "WETH")?.address.toLowerCase();
    const withLogos = base.map((t) => ({ ...t, logo: t.logo || logos[t.address === "native" ? weth : t.address.toLowerCase()] }));
    const seen = new Set(withLogos.map((t) => t.address.toLowerCase()));
    const rest = [...official, ...listed].filter((t) => !seen.has(t.address.toLowerCase()) && seen.add(t.address.toLowerCase()));
    return [...withLogos, ...rest];
  }, [base, listed, official, logos]);
  const { balances, extra } = useWalletHoldings(all);
  return useMemo(() => {
    const ethUsd = official.find((t) => t.symbol === "WETH" && t.price_usd)?.price_usd;
    return sortByHoldings([...all, ...extra], balances, ethUsd);
  }, [all, extra, balances, official]);
};
